import { OPERATOR_ACTIVITY } from "./mockData";
import { Building2, FileUp, UserPlus, Store, Loader2, AlertOctagon, FileText, PlayCircle, Archive, ArchiveRestore, Trash2, ShieldAlert, Activity } from "lucide-react";
import { SectionHeader } from "./ui";
import { useFetch } from "./api/useFetch";
import { adaptActivity, unwrap } from "./api/adapters";
import { isSurfaceLive } from "./api/platformClient";
import { LiveBadge } from "./ui-badges";

const ICONS = {
  company_created:        { icon: Building2,     tone: "bg-sky-100 text-sky-700" },
  tenant_draft_created:   { icon: FileText,      tone: "bg-indigo-100 text-indigo-700" },
  tenant_activated:       { icon: PlayCircle,    tone: "bg-emerald-100 text-emerald-700" },
  catalog_imported:       { icon: FileUp,        tone: "bg-violet-100 text-violet-700" },
  user_created:           { icon: UserPlus,      tone: "bg-emerald-100 text-emerald-700" },
  store_added:            { icon: Store,         tone: "bg-amber-100 text-amber-800" },
  company_archived:       { icon: Archive,       tone: "bg-amber-100 text-amber-800" },
  company_unarchived:     { icon: ArchiveRestore,tone: "bg-sky-100 text-sky-700" },
  company_deleted:        { icon: Trash2,        tone: "bg-rose-100 text-rose-700" },
  company_delete_dry_run: { icon: ShieldAlert,   tone: "bg-slate-100 text-slate-700" },
  event:                  { icon: Activity,      tone: "bg-slate-100 text-slate-700" },
};

const LABELS = {
  company_created: "Company created",
  tenant_draft_created: "Tenant draft created",
  tenant_activated: "Tenant activated",
  catalog_imported: "Catalog imported",
  user_created: "User created",
  store_added: "Store added",
  company_archived: "Tenant archived",
  company_unarchived: "Tenant unarchived",
  company_deleted: "Tenant deleted",
  company_delete_dry_run: "Tenant deletion dry-run",
};

export const OperatorActivity = ({ filterClient, companyId, compact }) => {
  const live = isSurfaceLive("ACTION_HISTORY");
  // ShelfLife requires `company_id` on /api/platform/action-history. Without
  // a selected tenant we don't fire the request (would be 422) and instead
  // render guidance asking the operator to pick a tenant.
  const needsTenantSelection = live && !companyId;
  const livePath = live && companyId
    ? `/api/platform/action-history?company_id=${encodeURIComponent(companyId)}`
    : null;
  const { data, error, loading } = useFetch(livePath);

  const liveItems = (() => {
    const list = unwrap(data, "entries");
    return Array.isArray(list) ? list.map(adaptActivity) : null;
  })();

  // Live mode is allowed to be empty (truthful "No activity recorded") OR to
  // surface a malformed-shape error. It is NEVER allowed to silently fall
  // back to demo/mock rows — that would mask real upstream contract drift.
  // Mock data is only used in non-live/demo mode.
  const malformedShape =
    live && !loading && !error && data !== null && data !== undefined && liveItems === null;

  const mockItems = !live
    ? (filterClient
        ? OPERATOR_ACTIVITY.filter((a) =>
            a.target.toLowerCase().includes(filterClient.toLowerCase())
          )
        : OPERATOR_ACTIVITY)
    : [];
  const items = live ? (liveItems || []) : mockItems;

  return (
    <div
      data-testid="operator-activity-panel"
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <SectionHeader
        title="Operator activity"
        subtitle={compact ? undefined : "Recent actions performed by Blue Harbor Labs operators"}
        right={<LiveBadge live={live} />}
      />

      {live && loading && (
        <div className="py-6 text-center">
          <Loader2 className="mx-auto h-5 w-5 text-slate-400 animate-spin" />
          <p className="mt-2 text-xs text-slate-500">Loading action history…</p>
        </div>
      )}

      {needsTenantSelection && (
        <div
          data-testid="operator-activity-needs-tenant"
          className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
        >
          Select a tenant from the Clients dashboard to view its action history.
          Platform-wide activity is not exposed at this scope.
        </div>
      )}

      {live && error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm flex items-start gap-2" data-testid="operator-activity-error">
          <AlertOctagon className="h-4 w-4 text-rose-700 mt-0.5" />
          <span className="text-rose-800">
            {error.kind === "forbidden" ? "Not authorized to view action history" : error.message}
          </span>
        </div>
      )}

      {malformedShape && (
        <div
          data-testid="operator-activity-malformed"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm flex items-start gap-2"
        >
          <AlertOctagon className="h-4 w-4 text-amber-700 mt-0.5" />
          <div className="text-amber-900">
            <p className="font-medium">Unexpected action-history response shape.</p>
            <p className="text-xs mt-1">
              ShelfLife returned a payload the Control Center could not interpret. Showing no rows rather than mock data.
              Report the response body to the platform team.
            </p>
          </div>
        </div>
      )}

      {!needsTenantSelection && !malformedShape && (!live || (!loading && !error)) && (
        <ol className="relative space-y-4">
          {items.map((a, idx) => {
            const meta = ICONS[a.type] || ICONS.event;
            const Icon = meta.icon;
            return (
              <li
                key={a.id}
                data-testid={`activity-item-${a.id}`}
                className="flex gap-3"
              >
                <div className="flex flex-col items-center">
                  <span className={`flex h-8 w-8 items-center justify-center rounded-full ${meta.tone}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  {idx < items.length - 1 && (
                    <span className="flex-1 w-px bg-slate-200 mt-1" />
                  )}
                </div>
                <div className="flex-1 pb-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-slate-900">
                      {LABELS[a.type] || a.typeLabel || "Event"}
                    </span>
                    <span className="text-xs text-slate-500">{a.time}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    <span className="font-medium text-slate-700">{a.actor}</span>
                    {" → "}
                    <span className="text-slate-700">{a.target}</span>
                  </div>
                  {a.description && (
                    <p className="text-sm text-slate-600 mt-1">{a.description}</p>
                  )}
                </div>
              </li>
            );
          })}
          {items.length === 0 && (
            <li className="text-sm text-slate-500 text-center py-6">
              No activity recorded.
            </li>
          )}
        </ol>
      )}
    </div>
  );
};
